import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaGift, FaRedo, FaClock, FaMoneyBillWave, FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';

const DailyRewards = () => {
  const [currentReward, setCurrentReward] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    fetchCurrentReward();
  }, []);

  const fetchCurrentReward = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get('https://api.globallequityfinance.com/api/admin/rewards/current');
      setCurrentReward(response.data);
    } catch (err) {
      setError('Failed to fetch current reward');
      console.error('Error fetching current reward:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const generateNewReward = async () => {
    try {
      setIsGenerating(true);
      setError(null);
      setSuccessMessage(null);
      
      await axios.post('https://api.globallequityfinance.com/api/admin/rewards/generate');
      
      // Reset current reward and fetch the new one
      await fetchCurrentReward();
      
      // Show success message
      setSuccessMessage('New reward successfully generated!');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
    } catch (err) {
      setError('Failed to generate new reward');
      console.error('Error generating new reward:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const formatExpiryTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  const calculateTimeRemaining = (dateString) => {
    const expiryTime = new Date(dateString).getTime();
    const currentTime = new Date().getTime();
    const difference = expiryTime - currentTime;
    
    if (difference <= 0) {
      return 'Expired';
    }
    
    // Convert to hours and minutes
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m remaining`;
  };

  return (
    <div className="forex-pesa-admin-daily-rewards">
      <div className="forex-pesa-admin-daily-rewards-header">
        <div className="forex-pesa-admin-daily-rewards-title-section">
          <h2 className="forex-pesa-admin-daily-rewards-title">
            <FaGift className="forex-pesa-admin-daily-rewards-title-icon" />
            Daily Rewards Management
          </h2>
          <p className="forex-pesa-admin-daily-rewards-subtitle">
            Generate and manage daily reward codes for your users
          </p>
        </div>
        
        <button 
          className="forex-pesa-admin-daily-rewards-refresh-button"
          onClick={fetchCurrentReward}
          disabled={isLoading}
        >
          <FaRedo className="forex-pesa-admin-daily-rewards-button-icon" />
          Refresh
        </button>
      </div>

      {successMessage && (
        <div className="forex-pesa-admin-daily-rewards-success">
          <FaCheck className="forex-pesa-admin-daily-rewards-success-icon" />
          {successMessage}
        </div>
      )}
      
      {error && (
        <div className="forex-pesa-admin-daily-rewards-error">
          <FaTimes className="forex-pesa-admin-daily-rewards-error-icon" />
          {error}
        </div>
      )}

      <div className="forex-pesa-admin-daily-rewards-content">
        {isLoading ? (
          <div className="forex-pesa-admin-daily-rewards-loading">
            <div className="forex-pesa-admin-daily-rewards-spinner"></div>
            <p>Loading reward data...</p>
          </div>
        ) : currentReward ? (
          <div className="forex-pesa-admin-daily-rewards-card">
            <div className="forex-pesa-admin-daily-rewards-card-header">
              <h3>Current Active Reward</h3>
              <div className="forex-pesa-admin-daily-rewards-status">
                {new Date(currentReward.expiresAt) > new Date() ? (
                  <span className="forex-pesa-admin-daily-rewards-status-active">Active</span>
                ) : (
                  <span className="forex-pesa-admin-daily-rewards-status-expired">Expired</span>
                )}
              </div>
            </div>
            
            <div className="forex-pesa-admin-daily-rewards-card-content">
              <div className="forex-pesa-admin-daily-rewards-code-container">
                <div className="forex-pesa-admin-daily-rewards-label">Reward Code</div>
                <div className="forex-pesa-admin-daily-rewards-code">
                  {currentReward.rewardCode}
                </div>
                <div className="forex-pesa-admin-daily-rewards-code-help">
                  Share this code with users to claim their daily reward
                </div>
              </div>
              
              <div className="forex-pesa-admin-daily-rewards-details">
                <div className="forex-pesa-admin-daily-rewards-detail-item">
                  <div className="forex-pesa-admin-daily-rewards-detail-icon">
                    <FaMoneyBillWave />
                  </div>
                  <div className="forex-pesa-admin-daily-rewards-detail-content">
                    <div className="forex-pesa-admin-daily-rewards-detail-label">Reward Amount</div>
                    <div className="forex-pesa-admin-daily-rewards-detail-value">
                      KSH {currentReward.rewardAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
                
                <div className="forex-pesa-admin-daily-rewards-detail-item">
                  <div className="forex-pesa-admin-daily-rewards-detail-icon">
                    <FaClock />
                  </div>
                  <div className="forex-pesa-admin-daily-rewards-detail-content">
                    <div className="forex-pesa-admin-daily-rewards-detail-label">Expiry Time</div>
                    <div className="forex-pesa-admin-daily-rewards-detail-value">
                      {formatExpiryTime(currentReward.expiresAt)}
                    </div>
                    <div className="forex-pesa-admin-daily-rewards-detail-secondary">
                      {calculateTimeRemaining(currentReward.expiresAt)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="forex-pesa-admin-daily-rewards-card-footer">
              <button 
                className="forex-pesa-admin-daily-rewards-generate-button"
                onClick={generateNewReward}
                disabled={isGenerating}
              >
                {isGenerating ? (
                  <>
                    <FaSpinner className="forex-pesa-admin-daily-rewards-spinner-icon" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FaGift className="forex-pesa-admin-daily-rewards-button-icon" />
                    Generate New Reward
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="forex-pesa-admin-daily-rewards-empty">
            <div className="forex-pesa-admin-daily-rewards-empty-icon">
              <FaGift />
            </div>
            <h3>No Active Reward Found</h3>
            <p>Generate a new daily reward for your users</p>
            <button 
              className="forex-pesa-admin-daily-rewards-generate-button"
              onClick={generateNewReward}
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <FaSpinner className="forex-pesa-admin-daily-rewards-spinner-icon" />
                  Generating...
                </>
              ) : (
                <>
                  <FaGift className="forex-pesa-admin-daily-rewards-button-icon" />
                  Generate Daily Reward
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <style jsx>{`

      /* Daily Rewards Component Styles */
.forex-pesa-admin-daily-rewards {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header Styles */
.forex-pesa-admin-daily-rewards-header {
  padding: 24px 30px;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.forex-pesa-admin-daily-rewards-title-section {
  flex: 1;
}

.forex-pesa-admin-daily-rewards-title {
  margin: 0 0 5px 0;
  font-size: 20px;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
  gap: 10px;
}

.forex-pesa-admin-daily-rewards-title-icon {
  color: #805ad5;
  font-size: 22px;
}

.forex-pesa-admin-daily-rewards-subtitle {
  margin: 0;
  color: #718096;
  font-size: 14px;
}

.forex-pesa-admin-daily-rewards-refresh-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: #f7fafc;
  color: #4a5568;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.forex-pesa-admin-daily-rewards-refresh-button:hover:not(:disabled) {
  background-color: #edf2f7;
  color: #3182ce;
}

.forex-pesa-admin-daily-rewards-refresh-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.forex-pesa-admin-daily-rewards-button-icon {
  font-size: 16px;
}

/* Success and Error Messages */
.forex-pesa-admin-daily-rewards-success,
.forex-pesa-admin-daily-rewards-error {
  margin: 0 30px;
  padding: 15px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 500;
  animation: slideInDown 0.3s ease;
}

@keyframes slideInDown {
  from {
    transform: translateY(-10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.forex-pesa-admin-daily-rewards-success {
  background-color: #def7ec;
  color: #0e9f6e;
  margin-top: 20px;
}

.forex-pesa-admin-daily-rewards-error {
  background-color: #fee2e2;
  color: #dc2626;
  margin-top: 20px;
}

.forex-pesa-admin-daily-rewards-success-icon,
.forex-pesa-admin-daily-rewards-error-icon {
  font-size: 18px;
}

/* Content Area */
.forex-pesa-admin-daily-rewards-content {
  padding: 20px 30px 30px;
}

/* Loading State */
.forex-pesa-admin-daily-rewards-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #718096;
}

.forex-pesa-admin-daily-rewards-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(128, 90, 213, 0.2);
  border-top-color: #805ad5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Empty State */
.forex-pesa-admin-daily-rewards-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  text-align: center;
}

.forex-pesa-admin-daily-rewards-empty-icon {
  width: 80px;
  height: 80px;
  background-color: #f7fafc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #805ad5;
  margin-bottom: 20px;
}

.forex-pesa-admin-daily-rewards-empty h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #2d3748;
  font-weight: 600;
}

.forex-pesa-admin-daily-rewards-empty p {
  margin: 0 0 25px 0;
  color: #718096;
  font-size: 14px;
}

/* Reward Card */
.forex-pesa-admin-daily-rewards-card {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);
}

.forex-pesa-admin-daily-rewards-card-header {
  padding: 20px 25px;
  background-color: #f7fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.forex-pesa-admin-daily-rewards-card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
}

.forex-pesa-admin-daily-rewards-status {
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 30px;
}

.forex-pesa-admin-daily-rewards-status-active {
  background-color: #def7ec;
  color: #0e9f6e;
}

.forex-pesa-admin-daily-rewards-status-expired {
  background-color: #fee2e2;
  color: #dc2626;
}

.forex-pesa-admin-daily-rewards-card-content {
  padding: 25px;
}

.forex-pesa-admin-daily-rewards-code-container {
  margin-bottom: 30px;
  text-align: center;
}

.forex-pesa-admin-daily-rewards-label {
  font-size: 14px;
  color: #718096;
  margin-bottom: 10px;
  font-weight: 500;
}

.forex-pesa-admin-daily-rewards-code {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #805ad5;
  background-color: #f7fafc;
  border: 1px dashed #cbd5e0;
  border-radius: 8px;
  padding: 15px 20px;
  display: inline-block;
  min-width: 200px;
  margin-bottom: 10px;
}

.forex-pesa-admin-daily-rewards-code-help {
  font-size: 12px;
  color: #718096;
}

.forex-pesa-admin-daily-rewards-details {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.forex-pesa-admin-daily-rewards-detail-item {
  display: flex;
  align-items: flex-start;
  gap: 15px;
}

.forex-pesa-admin-daily-rewards-detail-icon {
  width: 40px;
  height: 40px;
  background-color: #f7fafc;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #805ad5;
  font-size: 18px;
}

.forex-pesa-admin-daily-rewards-detail-content {
  flex: 1;
}

.forex-pesa-admin-daily-rewards-detail-label {
  font-size: 13px;
  color: #718096;
  margin-bottom: 5px;
}

.forex-pesa-admin-daily-rewards-detail-value {
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 4px;
}

.forex-pesa-admin-daily-rewards-detail-secondary {
  font-size: 12px;
  color: #805ad5;
  font-weight: 500;
}

.forex-pesa-admin-daily-rewards-card-footer {
  padding: 20px 25px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
}

.forex-pesa-admin-daily-rewards-generate-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #9f7aea, #805ad5);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.forex-pesa-admin-daily-rewards-generate-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #805ad5, #6b46c1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(128, 90, 213, 0.3);
}

.forex-pesa-admin-daily-rewards-generate-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.forex-pesa-admin-daily-rewards-spinner-icon {
  animation: spin 1s linear infinite;
}

/* Responsive Styles */
@media (max-width: 768px) {
  .forex-pesa-admin-daily-rewards-header {
    padding: 20px 25px;
    flex-direction: column;
    align-items: flex-start;
  }
  
  .forex-pesa-admin-daily-rewards-refresh-button {
    align-self: flex-end;
  }
  
  .forex-pesa-admin-daily-rewards-content {
    padding: 15px 25px 25px;
  }
  
  .forex-pesa-admin-daily-rewards-code {
    font-size: 24px;
  }
  
  .forex-pesa-admin-daily-rewards-details {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .forex-pesa-admin-daily-rewards-header {
    padding: 15px 20px;
  }
  
  .forex-pesa-admin-daily-rewards-content {
    padding: 10px 20px 20px;
  }
  
  .forex-pesa-admin-daily-rewards-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .forex-pesa-admin-daily-rewards-status {
    align-self: flex-start;
  }
  
  .forex-pesa-admin-daily-rewards-code {
    font-size: 20px;
    padding: 12px 15px;
  }
  
  .forex-pesa-admin-daily-rewards-card-footer {
    justify-content: center;
  }
}


      `}</style>

    </div>
  );
};

export default DailyRewards;